/**
 * PublicRequisitesForm - Публичная форма заполнения реквизитов
 *
 * Упрощённая версия RequisitesFormModal для внешних пользователей.
 * Без полей: контакты (director/contact_person), договорные поля, is_primary.
 */
import React, { useState } from 'react';
import {
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  Alert,
  Divider,
  SegmentedControl,
  Box,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBuildingBank,
  IconUser,
  IconSend,
} from '@tabler/icons-react';

// Функции валидации (идентичны RequisitesFormModal)
const validateInn = (inn, entityType) => {
  if (!inn) return 'ИНН обязателен';
  if (!/^\d+$/.test(inn)) return 'ИНН должен содержать только цифры';
  if (entityType === 'legal' && inn.length !== 10) {
    return 'ИНН юридического лица должен содержать 10 цифр';
  }
  if (entityType === 'individual' && inn.length !== 12) {
    return 'ИНН ИП должен содержать 12 цифр';
  }
  return null;
};

const validateKpp = (kpp, entityType) => {
  if (entityType === 'individual') return null;
  if (!kpp) return 'КПП обязателен для юридических лиц';
  if (!/^\d{9}$/.test(kpp)) return 'КПП должен содержать 9 цифр';
  return null;
};

const validateOgrn = (ogrn, entityType) => {
  if (!ogrn) return 'ОГРН обязателен';
  if (!/^\d+$/.test(ogrn)) return 'ОГРН должен содержать только цифры';
  if (entityType === 'legal' && ogrn.length !== 13) {
    return 'ОГРН юридического лица должен содержать 13 цифр';
  }
  if (entityType === 'individual' && ogrn.length !== 15) {
    return 'ОГРНИП должен содержать 15 цифр';
  }
  return null;
};

const validateBankAccount = (account) => {
  if (!account) return 'Расчётный счёт обязателен';
  if (!/^\d{20}$/.test(account)) return 'Расчётный счёт должен содержать 20 цифр';
  return null;
};

const validateBik = (bik) => {
  if (!bik) return 'БИК обязателен';
  if (!/^\d{9}$/.test(bik)) return 'БИК должен содержать 9 цифр';
  return null;
};

const PublicRequisitesForm = ({ onSubmit, loading = false }) => {
  const [formData, setFormData] = useState({
    entity_type: 'legal',
    company_name: '',
    inn: '',
    kpp: '',
    ogrn: '',
    bank_account: '',
    bank_name: '',
    bank_bik: '',
    legal_address: '',
    physical_address: '',
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);

  // Валидация поля в реальном времени
  const validateField = (name, value) => {
    let error = null;

    switch (name) {
      case 'inn':
        error = validateInn(value, formData.entity_type);
        break;
      case 'kpp':
        error = validateKpp(value, formData.entity_type);
        break;
      case 'ogrn':
        error = validateOgrn(value, formData.entity_type);
        break;
      case 'bank_account':
        error = validateBankAccount(value);
        break;
      case 'bank_bik':
        error = validateBik(value);
        break;
      case 'company_name':
        if (!value?.trim()) error = 'Название обязательно';
        break;
      case 'bank_name':
        if (!value?.trim()) error = 'Название банка обязательно';
        break;
      case 'legal_address':
        if (!value?.trim()) error = 'Юридический адрес обязателен';
        break;
      case 'physical_address':
        if (!value?.trim()) error = 'Физический адрес обязателен';
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error;
  };

  // Обработка изменения типа организации
  const handleEntityTypeChange = (value) => {
    setFormData(prev => ({
      ...prev,
      entity_type: value,
      kpp: value === 'individual' ? '' : prev.kpp,
    }));
    setErrors(prev => ({ ...prev, inn: null, kpp: null, ogrn: null }));
  };

  // Обработка изменения поля
  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  // Ввод только цифр
  const handleNumericChange = (name, value, maxLength) => {
    const digits = value.replace(/\D/g, '').slice(0, maxLength);
    handleChange(name, digits);
  };

  // Полная валидация формы
  const validateForm = () => {
    const newErrors = {};

    if (!formData.company_name?.trim()) {
      newErrors.company_name = 'Название обязательно';
    }

    const innError = validateInn(formData.inn, formData.entity_type);
    if (innError) newErrors.inn = innError;

    const kppError = validateKpp(formData.kpp, formData.entity_type);
    if (kppError) newErrors.kpp = kppError;

    const ogrnError = validateOgrn(formData.ogrn, formData.entity_type);
    if (ogrnError) newErrors.ogrn = ogrnError;

    const bankAccountError = validateBankAccount(formData.bank_account);
    if (bankAccountError) newErrors.bank_account = bankAccountError;

    if (!formData.bank_name?.trim()) {
      newErrors.bank_name = 'Название банка обязательно';
    }

    const bikError = validateBik(formData.bank_bik);
    if (bikError) newErrors.bank_bik = bikError;

    if (!formData.legal_address?.trim()) {
      newErrors.legal_address = 'Юридический адрес обязателен';
    }

    if (!formData.physical_address?.trim()) {
      newErrors.physical_address = 'Физический адрес обязателен';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const data = {
        entity_type: formData.entity_type,
        company_name: formData.company_name.trim(),
        inn: formData.inn.trim(),
        kpp: formData.entity_type === 'legal' ? formData.kpp.trim() : null,
        ogrn: formData.ogrn.trim(),
        bank_account: formData.bank_account.trim(),
        bank_name: formData.bank_name.trim(),
        bank_bik: formData.bank_bik.trim(),
        legal_address: formData.legal_address.trim(),
        physical_address: formData.physical_address.trim(),
        // Поля ниже не заполняются через публичную форму
        director_contact_id: null,
        contact_person_id: null,
        contract_start_date: null,
        class_days: null,
        class_time: null,
        is_primary: false,
      };

      await onSubmit(data);
    } catch (err) {
      setSubmitError(err.message || 'Ошибка при отправке данных');
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="md">
        {submitError && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {submitError}
          </Alert>
        )}

        {/* Тип организации */}
        <Box>
          <Text size="sm" fw={500} mb="xs">
            Тип организации
          </Text>
          <SegmentedControl
            value={formData.entity_type}
            onChange={handleEntityTypeChange}
            data={[
              {
                value: 'legal',
                label: (
                  <Group gap="xs">
                    <IconBuildingBank size={16} />
                    <span>Юридическое лицо</span>
                  </Group>
                ),
              },
              {
                value: 'individual',
                label: (
                  <Group gap="xs">
                    <IconUser size={16} />
                    <span>ИП</span>
                  </Group>
                ),
              },
            ]}
            fullWidth
            radius="xl"
          />
        </Box>

        <Divider label="Основные реквизиты" labelPosition="left" />

        {/* Название */}
        <TextInput
          label="Название компании/ИП"
          placeholder={formData.entity_type === 'legal' ? 'ООО "Ромашка"' : 'ИП Иванов И.И.'}
          required
          value={formData.company_name}
          onChange={(e) => handleChange('company_name', e.target.value)}
          error={errors.company_name}
        />

        {/* ИНН и КПП */}
        <Group grow>
          <TextInput
            label="ИНН"
            placeholder={formData.entity_type === 'legal' ? '10 цифр' : '12 цифр'}
            required
            value={formData.inn}
            onChange={(e) => handleNumericChange('inn', e.target.value,
              formData.entity_type === 'legal' ? 10 : 12
            )}
            error={errors.inn}
            maxLength={formData.entity_type === 'legal' ? 10 : 12}
          />
          {formData.entity_type === 'legal' && (
            <TextInput
              label="КПП"
              placeholder="9 цифр"
              required
              value={formData.kpp}
              onChange={(e) => handleNumericChange('kpp', e.target.value, 9)}
              error={errors.kpp}
              maxLength={9}
            />
          )}
        </Group>

        {/* ОГРН */}
        <TextInput
          label={formData.entity_type === 'legal' ? 'ОГРН' : 'ОГРНИП'}
          placeholder={formData.entity_type === 'legal' ? '13 цифр' : '15 цифр'}
          required
          value={formData.ogrn}
          onChange={(e) => handleNumericChange('ogrn', e.target.value,
            formData.entity_type === 'legal' ? 13 : 15
          )}
          error={errors.ogrn}
          maxLength={formData.entity_type === 'legal' ? 13 : 15}
        />

        <Divider label="Банковские реквизиты" labelPosition="left" />

        {/* Расчётный счёт */}
        <TextInput
          label="Расчётный счёт"
          placeholder="20 цифр"
          required
          value={formData.bank_account}
          onChange={(e) => handleNumericChange('bank_account', e.target.value, 20)}
          error={errors.bank_account}
          maxLength={20}
        />

        {/* Банк и БИК */}
        <Group grow>
          <TextInput
            label="Название банка"
            placeholder="АО «Сбербанк»"
            required
            value={formData.bank_name}
            onChange={(e) => handleChange('bank_name', e.target.value)}
            error={errors.bank_name}
          />
          <TextInput
            label="БИК"
            placeholder="9 цифр"
            required
            value={formData.bank_bik}
            onChange={(e) => handleNumericChange('bank_bik', e.target.value, 9)}
            error={errors.bank_bik}
            maxLength={9}
          />
        </Group>

        <Divider label="Адреса" labelPosition="left" />

        {/* Адреса */}
        <TextInput
          label="Юридический адрес"
          placeholder="Полный юридический адрес"
          required
          value={formData.legal_address}
          onChange={(e) => handleChange('legal_address', e.target.value)}
          error={errors.legal_address}
        />

        <TextInput
          label="Фактический адрес"
          placeholder="Полный фактический адрес"
          required
          value={formData.physical_address}
          onChange={(e) => handleChange('physical_address', e.target.value)}
          error={errors.physical_address}
        />

        {/* Кнопка отправки */}
        <Button
          type="submit"
          size="lg"
          fullWidth
          loading={loading}
          leftSection={<IconSend size={20} />}
          mt="md"
        >
          Отправить реквизиты
        </Button>
      </Stack>
    </form>
  );
};

export default PublicRequisitesForm;
